"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  CircularProgress,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import { Add, Edit, Delete } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import PageHeader from "../../components/common/PageHeader"
import EmptyState from "../../components/common/EmptyState"
import { getPlans, deletePlan } from "../../services/subscription.service"
import useOrganizationParams from "../../hooks/useOrganizationParams"

const Plans = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [planToDelete, setPlanToDelete] = useState(null)

  const organizationParams = useOrganizationParams()

  useEffect(() => {
    fetchPlans()
  }, [organizationParams])

  const fetchPlans = async () => {
    setLoading(true)
    try {
      const response = await getPlans(organizationParams)
      // Sort plans by tier_level (descending) and then by sortOrder
      const sortedPlans = response.data.sort((a, b) => {
        if (a.tier_level !== b.tier_level) {
          return (b.tier_level || 0) - (a.tier_level || 0)
        }
        return (a.sortOrder || 0) - (b.sortOrder || 0)
      })
      setPlans(sortedPlans)
    } catch (error) {
      showNotification("Failed to fetch plans", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePlan = () => {
    navigate("/plans/create")
  }

  const handleEditPlan = (id) => {
    navigate(`/plans/${id}`)
  }

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await deletePlan(planToDelete._id)
      showNotification("Plan deleted successfully", "success")
      fetchPlans()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to delete plan", "error")
    } finally {
      setDeleteDialogOpen(false)
      setPlanToDelete(null)
    }
  }

  // Helper function to get plan type label
  const getPlanTypeLabel = (plan) => {
    if (plan.planType === "free") return "Free Forever"
    if (plan.planType === "lifetime") return "Lifetime"
    if (plan.planType === "annual") return "Annual"
    return "Monthly"
  }

  // Helper function to get price display
  const getPriceDisplay = (plan) => {
    if (plan.planType === "free") return "Free"
    if (plan.planType === "lifetime") return `$${plan.lifetimePrice} one-time`
    if (plan.planType === "annual") return `$${plan.monthlyPrice}/mo or $${plan.annualPrice}/yr`
    return `$${plan.monthlyPrice}/mo`
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Subscription Plans"
        subtitle="Manage your subscription plans"
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleCreatePlan}
            sx={{ fontWeight: "bold" }}
          >
            Create Plan
          </Button>
        }
      />

      {/* Additional Create Plan button that's always visible */}
      {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<Add />}
          onClick={handleCreatePlan}
          sx={{
            fontWeight: "bold",
            px: 3,
            py: 1.2,
            boxShadow: 3,
          }}
        >
          Create New Plan
        </Button>
      </Box> */}

      {plans.length === 0 ? (
        <EmptyState
          title="No Plans Found"
          description="Get started by creating your first subscription plan."
          actionText="Create Plan"
          actionIcon={<Add />}
          onActionClick={handleCreatePlan}
        />
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} lg={4} key={plan._id}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" component="div">
                      {plan.name || "Unnamed Plan"}
                    </Typography>
                    <Box>
                      {plan.planCategory && (
                        <Chip
                          label={plan.planCategory === 'friends_family' ? 'Friends & Family' : plan.planCategory}
                          color={plan.planCategory === 'unlimited' ? 'warning' : (plan.planCategory === 'friends_family' ? 'secondary' : 'default')}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      <Chip
                        label={getPlanTypeLabel(plan)}
                        color={
                          plan.planType === "free" ? "success" : plan.planType === "lifetime" ? "secondary" : "primary"
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={plan.isActive ? "Active" : "Inactive"}
                        color={plan.isActive ? "success" : "default"}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={plan.isPublic ? "Public" : "Private"}
                        color={plan.isPublic ? "info" : "default"}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {plan.isDefault && (
                        <Chip
                          label="Default Plan"
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description || "No description available"}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      {getPriceDisplay(plan)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Features:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                    {plan.features && plan.features.length > 0 ? (
                      <>
                        {plan.features.slice(0, 5).map((feature, index) => (
                          <Typography component="li" variant="body2" key={index}>
                            {feature || ""}
                          </Typography>
                        ))}
                        {plan.features.length > 5 && (
                          <Typography variant="body2" color="text.secondary">
                            +{plan.features.length - 5} more features
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No features listed
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Credits: {plan.creditAllowance || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Max Users: {plan.maxUsers || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Tier Level: {plan.tier_level || 0}
                      </Typography>
                    </Grid>
                    {plan.planType !== "free" && plan.planType !== "lifetime" && plan.trialDays > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          {plan.trialDays} days free trial
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<Edit />} onClick={() => handleEditPlan(plan._id)}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDeleteClick(plan)}>
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the plan "{planToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Plans

